import AddressFormFields from '@/components/AddressFormFields';
import { SVTextInput, SVCheckbox, SVAutocomplete } from '@/components/core';
import { REGEX } from '@/constants/regex';
import { AddWireRecipientForm } from '@/types/wireRecipients';
import { Card, Typography, Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';

export default function WireRecipientsBankInfo() {
  const formMethods = useFormContext<AddWireRecipientForm>();
  const [internationalAddress, accountNumber, routingNumber] =
    formMethods.watch([
      'bankInfo.internationalBank',
      'bankInfo.accountNumber',
      'bankInfo.routingNumber',
    ]);

  return (
    <Card>
      <Typography variant="h5" mb={2}>
        Bank Info
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVTextInput
            label="Bank Name"
            name="bankInfo.bankName"
            rules={{ required: true }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVAutocomplete
            label="Account Type"
            name="bankInfo.accountType"
            rules={{ required: true }}
            options={[
              {
                label: 'Checking',
                value: 'checking',
              },
              {
                label: 'Savings',
                value: 'savings',
              },
            ]}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVTextInput
            label={
              internationalAddress ? 'Account Number / IBAN' : 'Account Number'
            }
            name="bankInfo.accountNumber"
            rules={{
              required: true,
              pattern: {
                regex: REGEX.CHECK_ACCOUNT_NUMBER,
                readablePatternName: 'number',
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVTextInput
            label={
              internationalAddress
                ? 'Confirm Account Number / IBAN'
                : 'Confirm Account Number'
            }
            name="bankInfo.confirmAccountNumber"
            rules={{
              required: true,
              pattern: {
                regex: REGEX.CHECK_ACCOUNT_NUMBER,
                readablePatternName: 'number',
              },
              validate: (value: string) => {
                if (value !== accountNumber) {
                  return 'Account Number and Confirm Account Number must match';
                }
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVTextInput
            label={internationalAddress ? 'SWIFT / BIC' : 'Routing Number'}
            name="bankInfo.routingNumber"
            rules={{
              required: true,
              pattern: {
                regex: internationalAddress
                  ? REGEX.CHECK_SWIFT_BIC_ROUTING_NUMBER
                  : REGEX.CHECK_US_ROUTING_NUMBER,
                readablePatternName: 'number',
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SVTextInput
            label={
              internationalAddress
                ? 'Confirm SWIFT / BIC'
                : 'Confirm Routing Number'
            }
            name="bankInfo.confirmRoutingNumber"
            rules={{
              required: true,
              pattern: {
                regex: internationalAddress
                  ? REGEX.CHECK_SWIFT_BIC_ROUTING_NUMBER
                  : REGEX.CHECK_US_ROUTING_NUMBER,
                readablePatternName: 'number',
              },
              validate: (value: string) => {
                if (value.trim() !== routingNumber?.trim()) {
                  return 'Routing Number and Confirm Routing Number must match';
                }
              },
            }}
          />
        </Grid>
      </Grid>
      <Typography variant="h6">Bank Address</Typography>
      <SVCheckbox
        label="International Bank?"
        name="bankInfo.internationalBank"
      />
      <AddressFormFields
        line1Name="bankInfo.line1"
        line2Name="bankInfo.line2"
        cityName="bankInfo.city"
        stateName="bankInfo.state"
        postalCodeName="bankInfo.zip"
        isInternational={internationalAddress}
        countryName="bankInfo.country"
      />
    </Card>
  );
}
